import React, { useEffect, useState } from "react";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useToast } from "../../hooks/use-toast";
import { Pencil, Trash2, Plus, Calendar } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../lib/authContext";

interface NonOperationalDay {
	id: number;
	non_operational_date: string;
	reason: string;
	created_at: string;
	updated_by: string | null;
	updated_by_email: string | null;
	is_delete: boolean;
}

export const NonOperationalDaysTab: React.FC = () => {
	const [nonOperationalDays, setNonOperationalDays] = useState<
		NonOperationalDay[]
	>([]);
	const [loading, setLoading] = useState(true);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editingDay, setEditingDay] = useState<NonOperationalDay | null>(null);
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
	const [dayToDelete, setDayToDelete] = useState<NonOperationalDay | null>(
		null,
	);
	const { toast } = useToast();
	const { user } = useAuth();

	const [formData, setFormData] = useState({
		non_operational_date: "",
		reason: "",
	});

	useEffect(() => {
		fetchNonOperationalDays();
	}, []);

	const fetchNonOperationalDays = async () => {
		try {
			const response = await api.getNonOperationalDays({
				limit: 100,
			});
			if (response.success) {
				setNonOperationalDays(response.data || []);
			} else {
				toast({
					variant: "destructive",
					title: "Error",
					description:
						response.error || "Failed to fetch non-operational days",
				});
			}
		} catch (error) {
			toast({
				variant: "destructive",
				title: "Error",
				description: "Failed to fetch non-operational days",
			});
		} finally {
			setLoading(false);
		}
	};

	const handleOpenModal = (day?: NonOperationalDay) => {
		if (day) {
			setEditingDay(day);
			setFormData({
				non_operational_date: day.non_operational_date,
				reason: day.reason,
			});
		} else {
			setEditingDay(null);
			setFormData({
				non_operational_date: "",
				reason: "",
			});
		}
		setIsModalOpen(true);
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
		setEditingDay(null);
		setFormData({
			non_operational_date: "",
			reason: "",
		});
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!user?.id) {
			toast({
				variant: "destructive",
				title: "Error",
				description: "User not authenticated",
			});
			return;
		}

		try {
			const payload = {
				...formData,
				user_id: user.id,
			};

			const response = editingDay
				? await api.updateNonOperationalDay(editingDay.id, payload)
				: await api.createNonOperationalDay(payload);

			if (response.success) {
				toast({
					title: "Success",
					description: editingDay
						? "Non-operational day updated successfully"
						: "Non-operational day created successfully",
				});
				handleCloseModal();
				fetchNonOperationalDays();
			} else {
				toast({
					variant: "destructive",
					title: "Error",
					description: response.error || "Failed to save non-operational day",
				});
			}
		} catch (error) {
			toast({
				variant: "destructive",
				title: "Error",
				description: "Failed to save non-operational day",
			});
		}
	};

	const handleDelete = async () => {
		if (!dayToDelete) return;

		if (!user?.id) {
			toast({
				variant: "destructive",
				title: "Error",
				description: "User not authenticated",
			});
			return;
		}

		try {
			const response = await api.deleteNonOperationalDay(dayToDelete.id, user.id);

			if (response.success) {
				toast({
					title: "Success",
					description: "Non-operational day deleted successfully",
				});
				setDeleteConfirmOpen(false);
				setDayToDelete(null);
				fetchNonOperationalDays();
			} else {
				toast({
					variant: "destructive",
					title: "Error",
					description: response.error || "Failed to delete non-operational day",
				});
			}
		} catch (error) {
			toast({
				variant: "destructive",
				title: "Error",
				description: "Failed to delete non-operational day",
			});
		}
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString("en-IN", {
			day: "2-digit",
			month: "short",
			year: "numeric",
		});
	};

	if (loading) {
		return <div className="p-6">Loading...</div>;
	}

	return (
		<div className="space-y-4">
			<div className="flex justify-end mb-4">
				<Button onClick={() => handleOpenModal()}>
					<Plus className="mr-2 h-4 w-4" /> Add Non Operational Day
				</Button>
			</div>

			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>Non Operational Date</TableHead>
						<TableHead>Reason</TableHead>
						<TableHead>Created At</TableHead>
						<TableHead>Updated By</TableHead>
						<TableHead>Actions</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{nonOperationalDays.length === 0 ? (
						<TableRow>
							<TableCell colSpan={5} className="text-center">
								No non-operational days found
							</TableCell>
						</TableRow>
					) : (
						nonOperationalDays.map((day) => (
							<TableRow key={day.id}>
								<TableCell className="font-medium">
									<div className="flex items-center">
										<Calendar className="mr-2 h-4 w-4 text-gray-500" />
										{formatDate(day.non_operational_date)}
									</div>
								</TableCell>
								<TableCell>{day.reason}</TableCell>
								<TableCell>{formatDate(day.created_at)}</TableCell>
								<TableCell>
									{day.updated_by_email || "N/A"}
								</TableCell>
								<TableCell>
									<div className="flex space-x-2">
										<Button
											variant="ghost"
											size="sm"
											onClick={() => handleOpenModal(day)}
										>
											<Pencil className="h-4 w-4" />
										</Button>
										<Button
											variant="ghost"
											size="sm"
											onClick={() => {
												setDayToDelete(day);
												setDeleteConfirmOpen(true);
											}}
										>
											<Trash2 className="h-4 w-4" />
										</Button>
									</div>
								</TableCell>
							</TableRow>
						))
					)}
				</TableBody>
			</Table>

			{/* Add/Edit Modal */}
			<Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
				<DialogContent className="sm:max-w-[425px]">
					<DialogHeader>
						<DialogTitle>
							{editingDay
								? "Edit Non Operational Day"
								: "Add Non Operational Day"}
						</DialogTitle>
						<DialogDescription>
							Define a date when operations will be non-operational (e.g.,
							holidays, festivals, maintenance days).
						</DialogDescription>
					</DialogHeader>
					<form onSubmit={handleSubmit}>
						<div className="grid gap-4 py-4">
							<div className="grid gap-2">
								<Label htmlFor="non_operational_date">Date</Label>
								<Input
									id="non_operational_date"
									type="date"
									value={formData.non_operational_date}
									onChange={(e) =>
										setFormData({
											...formData,
											non_operational_date: e.target.value,
										})
									}
									required
								/>
							</div>

							<div className="grid gap-2">
								<Label htmlFor="reason">Reason</Label>
								<Input
									id="reason"
									type="text"
									placeholder="e.g., Diwali, Sunday, Maintenance"
									value={formData.reason}
									onChange={(e) =>
										setFormData({
											...formData,
											reason: e.target.value,
										})
									}
									required
									maxLength={128}
								/>
							</div>
						</div>
						<DialogFooter>
							<Button type="button" variant="outline" onClick={handleCloseModal}>
								Cancel
							</Button>
							<Button type="submit">{editingDay ? "Update" : "Create"}</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>

			{/* Delete Confirmation Dialog */}
			<Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Confirm Delete</DialogTitle>
						<DialogDescription>
							Are you sure you want to delete this non-operational day? This
							action cannot be undone.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => {
								setDeleteConfirmOpen(false);
								setDayToDelete(null);
							}}
						>
							Cancel
						</Button>
						<Button variant="destructive" onClick={handleDelete}>
							Delete
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
};
